import type { HeldTicket } from '../tickets';
import { FairnessPrimaryButton } from './buttons';
import { FairnessSheet } from './FairnessSheet';
import { SeedField } from './SeedField';

const TITLE_ID = 'fairness-info-title';

const STEPS = [
  'Before you buy, Repackz locks in a secret server seed and shows you its SHA-256 hash below. The hash commits us to the seed without revealing it.',
  'When you open a pack, your browser adds its own random client seed.',
  'Together, the two seeds pick each card’s rarity tier from the published odds. After the pack opens, the server seed is revealed, so you can recompute every roll with Verify.',
];

export interface FairnessInfoModalProps {
  /** The ticket held for this pack's next purchase; null while it loads. */
  ticket: HeldTicket | null;
  onClose: () => void;
}

/** "Learn More" from the odds: how the roll works, with the commitment for the next purchase. */
export function FairnessInfoModal({ ticket, onClose }: FairnessInfoModalProps) {
  return (
    <FairnessSheet
      inspectName="FairnessInfoModal"
      titleId={TITLE_ID}
      title="Provably fair"
      onClose={onClose}
      footer={<FairnessPrimaryButton onClick={onClose}>Got it</FairnessPrimaryButton>}
    >
      <p className="text-[24px] font-semibold leading-[32px] tracking-[-0.4px]">Every rarity roll is provably fair</p>
      <ol className="flex list-decimal flex-col gap-2 pl-5">
        {STEPS.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
      <p className="text-[14px] leading-[20px]" style={{ color: 'var(--fairness-muted)' }}>
        Verify checks the rarity tier. Which card you get inside that tier is drawn from the cards in stock at that
        moment and is not part of the check.
      </p>
      <SeedField label="Next Server Seed Hash" value={ticket?.ticket.server_seed_hash ?? null} placeholder="Loading…" />
      <SeedField label="Your Next Client Seed" value={ticket?.clientSeed ?? null} placeholder="Loading…" />
    </FairnessSheet>
  );
}
